import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, SmilePlus, Palette, UsersRound, ArrowRight, TrendingUp, Star, ThumbsUp, BarChart3, Users } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_research-hub-218/artifacts/lk2wm99x_Skoop%21%20Logo%20-%202026.jpg";

/* ── Visitor Line Graph ── */
function VisitorLineGraph() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const W = 320, H = 160, padL = 10, padR = 10, padT = 10, padB = 25;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const lines = [
    { label: 'Local Visitors', color: '#0A192F', data: [42,38,45,52,61,72,78,74,65,53,44,40], delay: 0.3 },
    { label: 'Front Range Guests', color: '#38BDF8', data: [18,15,22,28,35,48,55,52,40,30,20,16], delay: 0.5 },
    { label: 'Destination Guests', color: '#166534', data: [8,6,12,20,32,45,58,62,48,25,12,7], delay: 0.7 },
    { label: 'International Guests', color: '#DC2626', data: [3,2,5,8,14,22,28,26,18,10,4,2], delay: 0.9 },
  ];

  const maxVal = 80;
  const xStep = (W - padL - padR) / (months.length - 1);

  const toPath = (data) => {
    return data.map((v, i) => {
      const x = padL + i * xStep;
      const y = padT + (1 - v / maxVal) * (H - padT - padB);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ');
  };

  return (
    <div ref={ref} className="h-48 sm:h-56 bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] p-4 relative overflow-hidden" style={{ perspective: '800px' }}>
      {/* Grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(0deg,#0A192F 0px,transparent 1px,transparent 20px),repeating-linear-gradient(90deg,#0A192F 0px,transparent 1px,transparent 20px)' }} />

      {/* Header */}
      <div
        className="flex items-center justify-between mb-2 relative z-10"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-15px)',
          transition: 'all 0.5s ease 0.1s',
        }}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Visitor Demographics</span>
        <span className="text-[9px] font-mono text-[#475569]">Annual %</span>
      </div>

      {/* SVG Chart */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full relative z-10" style={{ height: 'calc(100% - 32px)' }}>
        {/* Y grid lines */}
        {[0, 20, 40, 60, 80].map((v) => {
          const y = padT + (1 - v / maxVal) * (H - padT - padB);
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#0A192F" strokeOpacity="0.06" strokeWidth="0.5" />
              <text x={padL - 2} y={y + 3} fontSize="6" fill="#94A3B8" textAnchor="end" style={{ fontFamily: 'monospace' }}>{v}%</text>
            </g>
          );
        })}
        {/* X labels */}
        {months.map((m, i) => (
          <text key={m} x={padL + i * xStep} y={H - 5} fontSize="5.5" fill="#94A3B8" textAnchor="middle" style={{ fontFamily: 'monospace' }}>{m}</text>
        ))}
        {/* Lines */}
        {lines.map((line, li) => (
          <path
            key={li}
            d={toPath(line.data)}
            fill="none"
            stroke={line.color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 600,
              strokeDashoffset: visible ? 0 : 600,
              transition: `stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1) ${line.delay}s`,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
            }}
          />
        ))}
        {/* Dots on last point */}
        {lines.map((line, li) => {
          const lastVal = line.data[line.data.length - 1];
          const x = padL + (months.length - 1) * xStep;
          const y = padT + (1 - lastVal / maxVal) * (H - padT - padB);
          return (
            <circle
              key={li}
              cx={x} cy={y} r="3"
              fill={line.color}
              style={{
                opacity: visible ? 1 : 0,
                transition: `opacity 0.4s ease ${line.delay + 1.2}s`,
              }}
            />
          );
        })}
      </svg>

      {/* Legend */}
      <div
        className="absolute bottom-2 left-4 right-4 flex flex-wrap gap-x-4 gap-y-1 z-10"
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.5s ease 1.5s',
        }}
      >
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-[8px] font-semibold text-[#475569]">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 3D Satisfaction Graphic ── */
function SatisfactionGraphic3D() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const metrics = [
    { label: 'Overall', score: 4.8, pct: 96, color: '#166534' },
    { label: 'Service', score: 4.6, pct: 92, color: '#38BDF8' },
    { label: 'Value', score: 4.3, pct: 86, color: '#0A192F' },
    { label: 'Loyalty', score: 4.7, pct: 94, color: '#DC2626' },
  ];

  return (
    <div ref={ref} className="h-48 sm:h-56" style={{ perspective: '900px' }}>
      <div
        className="w-full h-full bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] p-5 flex flex-col justify-between relative overflow-hidden"
        style={{
          transform: visible ? 'rotateX(0) rotateY(0)' : 'rotateX(8deg) rotateY(-4deg)',
          transition: 'transform 0.8s cubic-bezier(0.22,1,0.36,1)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(0deg,#0A192F 0px,transparent 1px,transparent 20px),repeating-linear-gradient(90deg,#0A192F 0px,transparent 1px,transparent 20px)' }} />

        {/* Header */}
        <div
          className="flex items-center justify-between relative z-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0) translateZ(0)' : 'translateY(-30px) translateZ(40px)',
            transition: 'all 0.5s cubic-bezier(0.22,1,0.36,1) 0.15s',
          }}
        >
          <div className="flex items-center gap-1.5">
            <ThumbsUp size={13} className="text-[#166534]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Satisfaction Index</span>
          </div>
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(s => <Star key={s} size={10} className="text-[#38BDF8]" fill={s <= 4 ? '#38BDF8' : 'none'} />)}
          </div>
        </div>

        {/* Big number */}
        <div
          className="text-center relative z-10 -my-1"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0) translateZ(0) scale(1)' : 'translateY(-50px) translateZ(60px) scale(0.7)',
            transition: 'all 0.7s cubic-bezier(0.22,1,0.36,1) 0.3s',
          }}
        >
          <p className="text-4xl font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 2px 8px rgba(10,25,47,0.08)' }}>
            94<span className="text-lg">%</span>
          </p>
          <p className="text-[9px] font-semibold text-[#475569] uppercase tracking-wider">Avg Satisfaction</p>
        </div>

        {/* Metric bars */}
        <div className="space-y-1.5 relative z-10">
          {metrics.map((m, i) => (
            <div
              key={i}
              className="flex items-center gap-2"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0) translateZ(0)' : 'translateX(-20px) translateZ(20px)',
                transition: `all 0.5s cubic-bezier(0.22,1,0.36,1) ${0.5 + i * 0.1}s`,
              }}
            >
              <span className="text-[9px] font-semibold text-[#475569] w-12 text-right">{m.label}</span>
              <div className="flex-1 h-4 bg-white/60 rounded-md overflow-hidden" style={{ boxShadow: 'inset 0 1px 3px rgba(10,25,47,0.06)' }}>
                <div
                  className="h-full rounded-md"
                  style={{
                    width: visible ? `${m.pct}%` : '0%',
                    backgroundColor: m.color,
                    transition: `width 0.9s cubic-bezier(0.22,1,0.36,1) ${0.7 + i * 0.12}s`,
                    boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)',
                  }}
                />
              </div>
              <span className="text-[9px] font-bold text-[#0A192F] w-6">{m.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const services = [
  {
    icon: <CalendarDays size={32} />,
    title: 'Special Event Research',
    description: "Leave the guessing behind. Let us evaluate your event's performance, measure loyalty and recommendations (Net Promoter Score), capture detailed demographical and geographical insights, and evaluate your event's monetary impact. We capture the necessary data to make better decisions now and in the future.",
    color: 'bg-[#DC2626]',
    type: 'image',
    image: "https://images.pexels.com/photos/3727163/pexels-photo-3727163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: <SmilePlus size={32} />,
    title: 'Customer Satisfaction',
    description: 'It\'s a common saying in the research world; "You can\'t evaluate performance unless you measure it." We specialize in guest/customer satisfaction surveys, and offer models/benchmarks which will allow you to evaluate performance, identify trends, create objectives, and set future goals.',
    color: 'bg-[#166534]',
    type: 'graphic',
  },
  {
    icon: <Palette size={32} />,
    title: 'Brand Assessment',
    description: "Don't let anecdotal feedback and one-off comments drive your decisions. Evaluate your brand through unique questions and reporting; such as heat mapping or word graphing. These techniques are proven to unfold the strengths, weaknesses, opportunities, and brand perception.",
    color: 'bg-[#38BDF8]',
    type: 'image',
    image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxicmFuZGluZyUyMHNrZXRjaCUyMHN0cmF0ZWd5fGVufDB8fHx8MTc3NjI5NjAzNXww&ixlib=rb-4.1.0&q=85&w=600",
  },
  {
    icon: <UsersRound size={32} />,
    title: 'Research Panel',
    description: 'Have you ever had the need for an effective way to reach out to your community for feedback (e.g. community development, education, special events)? We work with our clients to develop and manage research panels; big or small, from one off "Nice-to-knows" to in-depth brand and community studies.',
    color: 'bg-[#0A192F]',
    type: 'lineGraph',
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="bg-[#0A192F] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={LOGO_URL} alt="SKOOP! Logo" className="h-24 w-auto mx-auto mb-8 animate-fade-up p-2 bg-white/10 rounded-2xl backdrop-blur-sm" style={{ filter: 'drop-shadow(0 0 30px rgba(56,189,248,0.3))' }} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-up animate-delay-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Research That Drives Results
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto animate-fade-up animate-delay-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
            From event evaluation to brand perception, we provide the data you need to make confident decisions.
          </p>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-20 sm:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className="neo-card overflow-hidden group"
              >
                {service.type === 'graphic' ? (
                  <SatisfactionGraphic3D />
                ) : service.type === 'lineGraph' ? (
                  <VisitorLineGraph />
                ) : (
                  <div className="h-48 sm:h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-8">
                  <div className={`w-14 h-14 rounded-xl ${service.color} ${service.color === 'bg-[#38BDF8]' ? 'text-[#0A192F]' : 'text-white'} flex items-center justify-center mb-5 -mt-14 relative z-10 border-2 border-[#0A192F] shadow-[3px_3px_0px_0px_rgba(10,25,47,1)]`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A192F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#475569] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A192F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Let us help you gather the insights you need. Reach out today.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="neo-btn inline-flex items-center gap-2 px-8 py-4 bg-[#38BDF8] text-[#0A192F] text-base"
          >
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
